import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Box, Button, Menu, Popover, TextField } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { format } from 'date-fns';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SortMenuItem } from 'features/documentList/components/TypeFilterDropdown/styled';
import { theme } from 'styles';

const CURRENT_YEAR = new Date().getFullYear();

interface DateRangeDropdownProps {
  value: string;
  onChange: (value: string) => void;
  onCustomApply: (start: Date | null, end: Date | null) => void;
  customStart: Date | null;
  customEnd: Date | null;
}

export const DateRangeDropdown = ({
  value,
  onChange,
  onCustomApply,
  customStart,
  customEnd,
}: DateRangeDropdownProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [customAnchorEl, setCustomAnchorEl] = useState<HTMLElement | null>(null);
  const [localStart, setLocalStart] = useState<Date | null>(customStart);
  const [localEnd, setLocalEnd] = useState<Date | null>(customEnd);
  const { t } = useTranslation();

  const PRESETS = [
    { value: '', label: t('resources.documentCategories.timeFilters.allTime') },
    { value: 'today', label: t('resources.documentCategories.timeFilters.today') },
    { value: 'last7days', label: t('resources.documentCategories.timeFilters.last7Days') },
    { value: 'last30days', label: t('resources.documentCategories.timeFilters.last30Days') },
    {
      value: 'thisyear',
      label: t('resources.documentCategories.timeFilters.thisYear', { year: CURRENT_YEAR }),
    },
    {
      value: 'lastyear',
      label: t('resources.documentCategories.timeFilters.lastYear', { year: CURRENT_YEAR - 1 }),
    },
  ];

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
    setLocalStart(customStart);
    setLocalEnd(customEnd);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setCustomAnchorEl(null);
  };

  const handlePreset = (preset: string) => {
    onChange(preset);
    handleClose();
  };

  const handleCustomClick = (e: React.MouseEvent<HTMLElement>) => {
    setCustomAnchorEl(e.currentTarget);
  };

  const handleApply = () => {
    onCustomApply(localStart, localEnd);
    handleClose();
  };

  const getLabel = () => {
    if (value === 'custom' && customStart && customEnd) {
      return `${format(customStart, 'MMM d')} – ${format(customEnd, 'MMM d, yyyy')}`;
    }
    if (value === 'custom') return 'Custom range';
    return PRESETS.find((p) => p.value === value)?.label ?? 'All time';
  };

  const isActive = Boolean(value);

  return (
    <>
      <Button
        onClick={handleOpen}
        sx={{
          height: '44px',
          minWidth: '160px',
          px: '14px',
          background: '#fff',
          border: `1px solid ${isActive ? theme.palette.info.main : '#E0E0E0'}`,
          borderRadius: '8px',
          fontSize: '14px',
          color: isActive ? theme.palette.info.main : '#333',
          fontWeight: isActive ? 500 : 400,
          textTransform: 'none',
          whiteSpace: 'nowrap',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '4px',
          '&:hover': { background: '#F5F8FF', borderColor: theme.palette.info.main },
        }}
      >
        {getLabel()}
        <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />
      </Button>

      <Menu
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            mt: '6px',
            minWidth: 230,
          },
        }}
      >
        {PRESETS.map((p) => {
          const isSelected = value === p.value && value !== 'custom';
          return (
            <SortMenuItem
              key={p.value}
              onClick={() => handlePreset(p.value)}
              selected={isSelected}
              sx={{
                fontSize: '14px',
                fontWeight: isSelected ? 500 : 400,
                color: isSelected ? theme.palette.info.main : '#333',
                '&.Mui-selected': { background: '#EAF5FF', '&:hover': { background: '#EAF5FF' } },
                '&:hover': { background: '#F5F8FF' },
              }}
            >
              {p.label}
            </SortMenuItem>
          );
        })}

        <SortMenuItem
          onClick={handleCustomClick}
          selected={value === 'custom'}
          sx={{
            fontSize: '14px',
            fontWeight: value === 'custom' ? 500 : 400,
            color: value === 'custom' ? theme.palette.info.main : '#333',
            justifyContent: 'space-between',
            '&.Mui-selected': { background: '#EAF5FF', '&:hover': { background: '#EAF5FF' } },
            '&:hover': { background: '#F5F8FF' },
          }}
        >
          {t('resources.documentCategories.timeFilters.customRange')}
          <ChevronRightIcon sx={{ fontSize: 18, color: theme.palette.info.main }} />
        </SortMenuItem>
      </Menu>

      <Popover
        open={Boolean(customAnchorEl)}
        anchorEl={customAnchorEl}
        onClose={() => setCustomAnchorEl(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            p: 2.5,
            width: 280,
          },
        }}
      >
        <Box display="flex" flexDirection="column" gap={2}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Start date"
              value={localStart}
              onChange={(d) => setLocalStart(d as Date | null)}
              inputFormat="dd/MM/yyyy"
              renderInput={(params) => <TextField {...params} size="small" fullWidth />}
            />
            <DatePicker
              label="End date"
              value={localEnd}
              onChange={(d) => setLocalEnd(d as Date | null)}
              inputFormat="dd/MM/yyyy"
              minDate={localStart ?? undefined}
              renderInput={(params) => <TextField {...params} size="small" fullWidth />}
            />
          </LocalizationProvider>

          <Box display="flex" justifyContent="flex-end" gap={1} mt={0.5}>
            <Button
              size="small"
              onClick={() => setCustomAnchorEl(null)}
              sx={{ color: '#666', textTransform: 'none' }}
            >
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleApply}
              disabled={!localStart || !localEnd}
              sx={{
                background: theme.palette.info.main,
                textTransform: 'none',
                borderRadius: '6px',
                '&:hover': { background: theme.palette.info.main },
                '&:disabled': { background: theme.palette.info.light, color: '#fff' },
              }}
            >
              Apply
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};
